import { PrescricaoType, Receituario, TipoReceitaSncr } from '_metronic/helpers/enums'
import type { PessoaList } from 'app/modules/auth'
import { isControleEspecial, tipoReceitaSncrDoItem } from '../receitaControlada'

/**
 * Categorias que o médico escolhe ao montar. "Especial" não aparece aqui: quem separa o
 * medicamento controlado numa receita própria é o backend (e `agruparEmDocumentos`, que o espelha).
 */
export type CategoriaItem =
  | PrescricaoType.Medicamento
  | PrescricaoType.Formula
  | PrescricaoType.Exame
  | PrescricaoType.Orientacoes

export const CATEGORIAS: CategoriaItem[] = [
  PrescricaoType.Medicamento,
  PrescricaoType.Formula,
  PrescricaoType.Exame,
  PrescricaoType.Orientacoes,
]

/** Item do catálogo, ou digitado agora pelo médico ("fora do catálogo", sem `id`). */
export type ItemCatalogo = {
  /** Sem `id`, o backend cadastra o item junto com a prescrição, com o médico como criador. */
  id?: number
  categoria: CategoriaItem
  nome: string
  concentracao?: string
  apresentacao?: string
  /** Classe terapêutica (só medicamento). */
  classe?: string
  tarja?: string
  /** Receituário exigido (Portaria 344/98); quando presente, manda sobre a tarja. */
  receituario?: Receituario
  controleEspecial?: boolean
  lme?: boolean
  apac?: boolean
  /** Tipo do exame, texto livre do cadastro; é ele que separa os pedidos de exame. */
  tipoExame?: string
  codigo?: string
  /** Composição da fórmula (campo `descricao` no backend). */
  composicao?: string
  /** Texto-modelo da orientação (campo `descricao` no backend). */
  textoPadrao?: string
  criador?: PessoaList
  ativo?: boolean
}

type ItemBase = {
  /** Identidade do item na tela (o mesmo medicamento pode não ter `id`). */
  chave: string
  catalogo: ItemCatalogo
}

export type ItemMedicamento = ItemBase & {
  categoria: PrescricaoType.Medicamento
  posologia: string
  quantidade: string
  observacoes: string
  usoContinuo: boolean
}

export type ItemFormula = ItemBase & {
  categoria: PrescricaoType.Formula
  posologia: string
  quantidade: string
  observacoes: string
}

export type ItemExame = ItemBase & {
  categoria: PrescricaoType.Exame
  instrucoes: string
  observacoes: string
}

export type ItemOrientacao = ItemBase & {
  categoria: PrescricaoType.Orientacoes
  texto: string
}

export type ItemPrescricao = ItemMedicamento | ItemFormula | ItemExame | ItemOrientacao

/** Campos que o médico preenche (cada categoria usa uma parte deles). */
export type ValoresItem = Partial<{
  posologia: string
  quantidade: string
  observacoes: string
  usoContinuo: boolean
  instrucoes: string
  texto: string
}>

let sequencia = 0
const novaChave = () => `item-${++sequencia}`

/** Cria o item da montagem a partir do catálogo, com os campos em branco ou os de `valores`. */
export const criarItem = (catalogo: ItemCatalogo, valores: ValoresItem = {}): ItemPrescricao => {
  const base = { chave: novaChave(), catalogo }
  switch (catalogo.categoria) {
    case PrescricaoType.Medicamento:
      return {
        ...base,
        categoria: PrescricaoType.Medicamento,
        posologia: valores.posologia ?? '',
        quantidade: valores.quantidade ?? '',
        observacoes: valores.observacoes ?? '',
        usoContinuo: valores.usoContinuo ?? false,
      }
    case PrescricaoType.Formula:
      return {
        ...base,
        categoria: PrescricaoType.Formula,
        posologia: valores.posologia ?? '',
        quantidade: valores.quantidade ?? '',
        observacoes: valores.observacoes ?? '',
      }
    case PrescricaoType.Exame:
      return {
        ...base,
        categoria: PrescricaoType.Exame,
        instrucoes: valores.instrucoes ?? '',
        observacoes: valores.observacoes ?? '',
      }
    case PrescricaoType.Orientacoes:
      return {
        ...base,
        categoria: PrescricaoType.Orientacoes,
        texto: valores.texto ?? catalogo.textoPadrao ?? '',
      }
  }
}

/** "Losartana - 50 mg": como o item aparece em listas e mensagens. */
export const rotuloCatalogo = (catalogo: Pick<ItemCatalogo, 'nome' | 'concentracao'>) =>
  catalogo.concentracao ? `${catalogo.nome} - ${catalogo.concentracao}` : catalogo.nome

export type Selo = 'controle' | 'retencao' | 'nra' | 'nrb' | 'nrb2' | 'nrr' | 'lme' | 'apac'

/** Selo de cada notificação de receita (A, B, B2, retinoides). */
const SELO_NOTIFICACAO: Partial<Record<Receituario, Selo>> = {
  [Receituario.NRA]: 'nra',
  [Receituario.NRB]: 'nrb',
  [Receituario.NRB2]: 'nrb2',
  [Receituario.NRR]: 'nrr',
}

/**
 * Selos legais do item: todo receituário que não é o simples, inclusive retenção (a farmácia
 * fica com uma via). Notificação tem selo próprio; RCE é "controle especial". Item sem
 * receituário cai na regra da tarja. LME e APAC só valem quando a unidade atende SUS.
 */
export const selosDoItem = (catalogo: ItemCatalogo, atendeSUS: boolean): Selo[] => {
  const selos: Selo[] = []
  if (catalogo.categoria === PrescricaoType.Medicamento) {
    const notificacao = catalogo.receituario && SELO_NOTIFICACAO[catalogo.receituario]
    const sncr = tipoReceitaSncrDoItem(PrescricaoType.Medicamento, catalogo)
    if (notificacao) selos.push(notificacao)
    else if (isControleEspecial(catalogo)) selos.push('controle')
    else if (sncr === TipoReceitaSncr.RET) selos.push('retencao')
    if (atendeSUS && catalogo.lme) selos.push('lme')
  }
  if (catalogo.categoria === PrescricaoType.Exame && atendeSUS && catalogo.apac) selos.push('apac')
  return selos
}

/** O médico só altera o cadastro do que ele mesmo criou, ou do que digitou agora. */
export const podeEditarCadastro = (catalogo: ItemCatalogo, pessoaId?: number) =>
  !catalogo.id || (!!catalogo.criador && catalogo.criador.id === pessoaId)

/** Mesmo item do catálogo já está na prescrição (itens digitados agora nunca colidem). */
export const mesmoItemDoCatalogo = (a: ItemCatalogo, b: ItemCatalogo) =>
  !!a.id && a.id === b.id && a.categoria === b.categoria
